import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Upload, X, RotateCw, Crop, ScanLine, FileText, Image as ImageIcon, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function InvoiceCapture() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [captureMode, setCaptureMode] = useState('camera'); // 'camera', 'file', 'batch'
  const [batchFiles, setBatchFiles] = useState([]);

  // Handle file selection
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (captureMode === 'batch') {
      // Batch mode - collect multiple files
      const newBatch = [...batchFiles];
      files.forEach(file => {
        if (validateFile(file)) {
          newBatch.push({
            file,
            preview: URL.createObjectURL(file),
            rotation: 0
          });
        }
      });
      setBatchFiles(newBatch);
      toast.success(`${files.length} file(s) added to batch`);
    } else {
      // Single file mode
      const file = files[0];
      if (!validateFile(file)) return;
      
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      setRotation(0);
    }
  }, [captureMode, batchFiles]);

  // Validate file type and size
  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, WebP, or PDF');
      return false;
    }

    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 10MB');
      return false;
    }

    return true;
  };

  // Rotate image
  const handleRotate = () => {
    if (captureMode === 'batch') {
      // Rotate last added batch item
      if (batchFiles.length === 0) return;
      const newBatch = [...batchFiles];
      const lastIndex = newBatch.length - 1;
      newBatch[lastIndex].rotation = (newBatch[lastIndex].rotation + 90) % 360;
      setBatchFiles(newBatch);
    } else {
      setRotation((prev) => (prev + 90) % 360);
    }
  };

  // Clear selection
  const handleClear = () => {
    setPreview(null);
    setSelectedFile(null);
    setRotation(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  // Remove batch item
  const removeBatchItem = (index) => {
    const newBatch = batchFiles.filter((_, i) => i !== index);
    setBatchFiles(newBatch);
  };

  // Upload single file
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await api.post('/invoices/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minute timeout for AI processing
      });

      toast.success('Invoice uploaded successfully!');
      
      const invoiceId = response.data?.id || response.data?._id || response.data?.data?.id;
      if (invoiceId) {
        navigate(`/invoices/${invoiceId}/review`);
      } else {
        toast.error('Invoice uploaded but failed to navigate to review page');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload invoice';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  // Upload batch files
  const handleBatchUpload = async () => {
    if (batchFiles.length === 0) {
      toast.error('Please add files to batch first');
      return;
    }

    setUploading(true);
    const results = [];
    const errors = [];

    for (let i = 0; i < batchFiles.length; i++) {
      const batchItem = batchFiles[i];
      toast.info(`Uploading file ${i + 1} of ${batchFiles.length}...`);

      const formData = new FormData();
      formData.append('file', batchItem.file);

      try {
        const response = await api.post('/invoices/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });
        results.push(response.data);
        toast.success(`File ${i + 1} uploaded successfully`);
      } catch (error) {
        console.error(`Batch upload error for file ${i + 1}:`, error);
        errors.push({ file: batchItem.file.name, error: error.response?.data?.detail || 'Upload failed' });
        toast.error(`File ${i + 1} failed to upload`);
      }

      // Small delay between uploads to avoid rate limits
      if (i < batchFiles.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    setUploading(false);

    if (errors.length === 0) {
      toast.success(`All ${results.length} invoices uploaded successfully!`);
      navigate('/invoices');
    } else {
      toast.warning(`${results.length} uploaded, ${errors.length} failed. Check console for details.`);
      console.log('Batch upload errors:', errors);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Capture Invoice</h1>
        <p className="text-muted-foreground">Upload or scan your invoice for AI processing</p>
      </div>

      {/* Capture Mode Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { setCaptureMode('camera'); handleClear(); setBatchFiles([]); }}
          className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
            captureMode === 'camera' 
              ? 'bg-blue-600 text-white' 
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <Camera className="w-5 h-5" />
          Camera
        </button>
        <button
          onClick={() => { setCaptureMode('file'); handleClear(); setBatchFiles([]); }}
          className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
            captureMode === 'file' 
              ? 'bg-blue-600 text-white' 
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <FileText className="w-5 h-5" />
          Document
        </button>
        <button
          onClick={() => { setCaptureMode('batch'); handleClear(); }}
          className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
            captureMode === 'batch' 
              ? 'bg-blue-600 text-white' 
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <ScanLine className="w-5 h-5" />
          Batch ({batchFiles.length})
        </button>
      </div>

      {/* Upload Area */}
      {!preview && batchFiles.length === 0 && (
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 transition-colors">
          <div className="mb-4">
            {captureMode === 'camera' ? (
              <Camera className="w-16 h-16 mx-auto text-gray-400" />
            ) : captureMode === 'file' ? (
              <FileText className="w-16 h-16 mx-auto text-gray-400" />
            ) : (
              <ScanLine className="w-16 h-16 mx-auto text-gray-400" />
            )}
          </div>
          
          <p className="text-lg font-medium mb-2">
            {captureMode === 'camera' 
              ? 'Take a photo of your invoice' 
              : captureMode === 'file'
              ? 'Upload invoice document'
              : 'Upload multiple invoices'}
          </p>
          
          <p className="text-sm text-gray-500 mb-4">
            Supports JPG, PNG, WebP, and PDF (max 10MB)
          </p>

          <div className="flex gap-3 justify-center">
            {captureMode === 'camera' && (
              <>
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Camera className="w-5 h-5" />
                  Open Camera
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-6 py-3 border border-border rounded-lg hover:bg-muted flex items-center gap-2"
                >
                  <ImageIcon className="w-5 h-5" />
                  Gallery
                </button>
              </>
            )}
            
            {(captureMode === 'file' || captureMode === 'batch') && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Upload className="w-5 h-5" />
                {captureMode === 'batch' ? 'Add Files to Batch' : 'Select File'}
              </button>
            )}
          </div>

          {/* Hidden inputs */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            multiple={captureMode === 'batch'}
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Single File Preview */}
      {preview && captureMode !== 'batch' && (
        <div className="bg-card rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Preview</h3>
            <button
              onClick={handleClear}
              className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="relative bg-muted rounded-lg overflow-hidden mb-4" style={{ maxHeight: '500px' }}>
            {selectedFile?.type === 'application/pdf' ? (
              <div className="flex items-center justify-center h-64">
                <FileText className="w-16 h-16 text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">{selectedFile.name}</span>
              </div>
            ) : (
              <img
                src={preview}
                alt="Invoice preview"
                className="w-full h-auto max-h-96 object-contain mx-auto transition-transform"
                style={{ transform: `rotate(${rotation}deg)` }}
              />
            )}
          </div>

          {/* Toolbar */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={handleRotate}
              disabled={selectedFile?.type === 'application/pdf'}
              className="flex-1 py-2 px-4 border rounded-lg hover:bg-muted flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <RotateCw className="w-4 h-4" />
              Rotate
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 py-2 px-4 border rounded-lg hover:bg-muted flex items-center justify-center gap-2"
            >
              <Crop className="w-4 h-4" />
              Replace
            </button>
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing with AI...
              </>
            ) : (
              <>
                <ScanLine className="w-5 h-5" />
                Extract with AI
              </>
            )}
          </button>
        </div>
      )}

      {/* Batch Preview */}
      {captureMode === 'batch' && batchFiles.length > 0 && (
        <div className="bg-card rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Batch ({batchFiles.length} files)</h3>
            <button
              onClick={() => setBatchFiles([])}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 max-h-96 overflow-y-auto">
            {batchFiles.map((item, index) => (
              <div key={index} className="relative bg-muted rounded-lg overflow-hidden">
                {item.file.type === 'application/pdf' ? (
                  <div className="flex items-center justify-center h-32">
                    <FileText className="w-8 h-8 text-muted-foreground" />
                  </div>
                ) : (
                  <img
                    src={item.preview}
                    alt={`Batch item ${index + 1}`}
                    className="w-full h-32 object-cover"
                    style={{ transform: `rotate(${item.rotation}deg)` }}
                  />
                )}
                <button
                  onClick={() => removeBatchItem(index)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
                  {item.file.name}
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 py-2 px-4 border rounded-lg hover:bg-muted"
            >
              + Add More
            </button>
            <button
              onClick={handleBatchUpload}
              disabled={uploading}
              className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Upload All
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="mt-6 bg-muted rounded-lg p-4 border border-border">
        <h4 className="font-medium text-foreground mb-2">Tips for best results:</h4>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• Ensure good lighting when taking photos</li>
          <li>• Keep the invoice flat and centered</li>
          <li>• Make sure all text is clearly visible</li>
          <li>• For multi-page invoices, use the Document or Batch mode</li>
          <li>• AI works best with printed text (handwritten may have lower accuracy)</li>
        </ul>
      </div>
    </div>
  );
}
